import Search from "@mui/icons-material/Search";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { InputAdornment, TextField, ToggleButton, Tooltip } from "@mui/material";

/**
 * Compact filter controls that sit on a section card's header line: a small
 * search box and an over-threshold toggle.
 *
 * Deliberately NOT omnisearch. These sections hold a handful of cards per
 * facility, so the heavyweight filter bar would out-weigh what it filters;
 * the search is a plain substring match handled by the caller.
 *
 * The metrics match `ExpandableSearchInput` (32px tall, 0.875rem) rather than
 * introducing a fourth input size for one page.
 */
const COMPACT_INPUT_HEIGHT = 32;
const COMPACT_FONT_SIZE = "0.875rem";

export function SectionFilters({
  search,
  onSearchChange,
  overThreshold,
  searchLabel,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  /**
   * The over-threshold toggle, rendered only when the caller supplies it.
   *
   * Optional because the filter is meaningless for a central area: its volume
   * has no cap, so `AreaVolume.status` resolves to OK for every one of them
   * (`volumeComponentStatus` returns OK on a null threshold, and the satellite
   * limit is the only thing that sets one). A toggle there would empty the
   * section every time it was switched on.
   */
  overThreshold?: { only: boolean; onChange: (value: boolean) => void };
  /** Accessible name for the box, since the placeholder alone repeats across
   * both sections and would leave two identically-named fields on the page. */
  searchLabel: string;
}) {
  return (
    <>
      <TextField
        variant="outlined"
        size="small"
        placeholder="Search"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        slotProps={{
          input: {
            sx: { height: COMPACT_INPUT_HEIGHT, fontSize: COMPACT_FONT_SIZE },
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          },
          htmlInput: { "aria-label": searchLabel },
        }}
        sx={{ width: 200 }}
      />
      {overThreshold && (
        <Tooltip title="Show only accumulation areas that are Over Threshold">
          <ToggleButton
            value="overThreshold"
            size="small"
            selected={overThreshold.only}
            onChange={() => overThreshold.onChange(!overThreshold.only)}
            aria-label="Show only accumulation areas that are Over Threshold"
            sx={{ height: COMPACT_INPUT_HEIGHT, width: COMPACT_INPUT_HEIGHT }}
          >
            <WarningAmberRounded fontSize="small" />
          </ToggleButton>
        </Tooltip>
      )}
    </>
  );
}
